import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LoginPage from "@/components/LoginPage";
import OverviewDashboard from "@/components/OverviewDashboard";
import FleetMapView from "@/components/FleetMapView";
import DeviceListView from "@/components/DeviceListView";
import ShipmentsView from "@/components/ShipmentsView";
import ShipmentDetail from "@/components/ShipmentDetail";
import Dashboard from "@/components/Dashboard";
import FileUpload from "@/components/FileUpload";
import ThresholdSettings from "@/components/ThresholdSettings";
import InviteUsers from "@/components/InviteUsers";
import AdminPanel from "@/components/AdminPanel";
import WebhooksManager from "@/components/WebhooksManager";
import LoggerWebhookManager from "@/components/LoggerWebhookManager";
import PartiesManager from "@/components/PartiesManager";
import NotificationRulesManager from "@/components/NotificationRulesManager";
import AccountabilityDashboard from "@/components/AccountabilityDashboard";
import AlertCenter from "@/components/AlertCenter";
import AuditTrailView from "@/components/AuditTrailView";
import DeviceRegistry from "@/components/DeviceRegistry";
import GeofenceManager from "@/components/GeofenceManager";
import SuperAdminPanel from "@/components/SuperAdminPanel";
import PilotAcceptanceGate, { PilotExpirationBanner } from "@/components/PilotAcceptanceGate";
import PilotManagementPage from "@/components/PilotManagementPage";
import OrgSwitcher, { ActingAsBanner } from "@/components/OrgSwitcher";
import ForcePasswordChangeModal from "@/components/ForcePasswordChangeModal";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Upload, Gear, SignOut, UsersThree, ShieldCheck,
  ChartBar, MapTrifold, List, Cpu, Package, Plugs as Webhook,
  Presentation, Buildings, Bell, Lightning, DotsThreeVertical,
  ClockCounterClockwise,
  MapPinArea,
  FilePdf, FileCsv,
} from "@phosphor-icons/react";

async function downloadPitchDeck() {
  const t = toast.loading("Generating pitch deck…");
  try {
    const token = localStorage.getItem("auth_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/pitch-deck/`;
    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });
    const cd = res.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const filename = m ? m[1] : `Holotrak_PitchDeck.pptx`;
    const blob = new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(link.href), 1500);
    toast.success("Pitch deck downloaded", { id: t });
  } catch (e) {
    toast.error("Pitch deck generation failed", { id: t });
  }
}

// Views: overview | map | devices | shipments | shipment_detail | detail | upload | admin
function AppContent() {
  const { user, loading: authLoading, logout } = useAuth();
  const [view, setView] = useState("overview");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedDatasetId, setSelectedDatasetId] = useState(null);
  const [selectedShipmentId, setSelectedShipmentId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showInvites, setShowInvites] = useState(false);
  const [showWebhooks, setShowWebhooks] = useState(false);
  const [showLoggerWebhooks, setShowLoggerWebhooks] = useState(false);
  const [showParties, setShowParties] = useState(false);
  const [showRouting, setShowRouting] = useState(false);
  const [showSuperAdmin, setShowSuperAdmin] = useState(false);
  const [pendingEnrollOrgId, setPendingEnrollOrgId] = useState(null);
  const [showDevices, setShowDevices] = useState(false);
  const [showGeofences, setShowGeofences] = useState(false);

  // Lightweight nav bus — any deeply-nested component (e.g. the Activation
  // Wizard inside the lifecycle banner inside ShipmentDetail) can fire a
  // CustomEvent("holotrak:nav", { detail: { target: "parties" } }) and we
  // surface the right modal here, instead of prop-drilling setters.
  useEffect(() => {
    const handler = (e) => {
      const target = e?.detail?.target;
      switch (target) {
        case "parties":            setShowParties(true); break;
        case "facilities":         setShowParties(true); break;   // Parties owns facilities
        case "routing":
        case "event_orchestration": setShowRouting(true); break;
        case "thresholds":         setShowSettings(true); break;
        case "webhooks":           setShowWebhooks(true); break;
        case "geofences":          setShowGeofences(true); break;
        default:                   break;
      }
    };
    window.addEventListener("holotrak:nav", handler);
    return () => window.removeEventListener("holotrak:nav", handler);
  }, []);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <img src="/holotrak-logo.png" alt="Holotrak" className="h-8 animate-pulse" />
      </div>
    );
  }

  if (!user) return <LoginPage />;

  const handleSelectDevice = (deviceName, datasetId) => {
    setSelectedDevice(deviceName);
    setSelectedDatasetId(datasetId);
    setView("detail");
  };

  const handleSelectShipment = (shipmentId) => {
    setSelectedShipmentId(shipmentId);
    setView("shipment_detail");
  };

  const navItems = [
    { key: "overview", label: "Dashboard", icon: ChartBar },
    { key: "map", label: "Map", icon: MapTrifold },
    { key: "shipments", label: "Shipments", icon: Package },
    { key: "devices", label: "Devices", icon: List },
    { key: "alerts", label: "Alerts", icon: Bell },
    { key: "accountability", label: "Accountability", icon: Lightning },
    { key: "audit", label: "Audit", icon: ClockCounterClockwise },
  ];

  // `isAdminRole` = can use day-to-day operational admin tools
  //   (Parties, Event Orchestration, Webhooks, Team Invites, Pitch Deck).
  //   Includes org_admin so RTE Pilot's admin (Robert) can run his own tenant.
  // `isSuperAdmin` = platform-level (tenants management, org switcher).
  //   Restricted to super_admin / legacy admin only.
  const isAdminRole = ["admin", "super_admin", "org_admin"].includes(user?.role);
  const isSuperAdmin = ["admin", "super_admin"].includes(user?.role);

  return (
    <PilotAcceptanceGate user={user} onLogout={logout}>
    <div className="min-h-screen bg-[#1A1A1A]">
      <Toaster theme="dark" position="top-right" />
      <PilotExpirationBanner user={user} />

      {/* Header */}
      <header data-testid="app-header"
        className="bg-zinc-950 border-b border-zinc-800 px-4 md:px-6 py-2.5 flex items-center justify-between gap-2 sticky top-0 z-50">
        <div className="flex items-center gap-3 md:gap-4 lg:gap-6 min-w-0">
          <img src="/holotrak-logo.png" alt="Holotrak" className="h-7 cursor-pointer flex-shrink-0"
            onClick={() => setView("overview")} data-testid="header-logo" />

          {/* Primary Navigation — hidden below lg (1024px) to prevent overlap
              with the right-side action buttons. Below lg the user accesses
              the same destinations through the action buttons + Admin Tools
              menu, which always remain icon-only on narrow viewports. */}
          <nav className="hidden lg:flex items-center gap-1 min-w-0 overflow-x-auto">
            {navItems.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setView(key)}
                className={`flex items-center gap-1.5 px-2 xl:px-3 py-1.5 text-xs font-medium rounded-sm transition-colors whitespace-nowrap ${
                  view === key ? "bg-teal-500/10 text-teal-400" : "text-zinc-500 hover:text-zinc-300"
                }`} data-testid={`nav-${key}`}>
                <Icon size={14} weight="duotone" />
                {label}
              </button>
            ))}
            {view === "detail" && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm bg-teal-500/10 text-teal-400 whitespace-nowrap">
                <Cpu size={14} weight="duotone" />
                {selectedDevice || "Device Detail"}
              </span>
            )}
            {view === "shipment_detail" && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm bg-teal-500/10 text-teal-400 whitespace-nowrap">
                <Package size={14} weight="duotone" />
                Shipment Detail
              </span>
            )}
          </nav>

          {/* Mobile / tablet (< lg): primary nav collapses into a dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm"
                className="lg:hidden border-zinc-700 hover:bg-zinc-800 h-8 px-2"
                data-testid="mobile-nav-trigger" aria-label="Open navigation">
                <List size={16} weight="bold" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52 bg-zinc-950 border-zinc-800 z-[2000]">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500">
                Navigate
              </DropdownMenuLabel>
              {navItems.map(({ key, label, icon: Icon }) => (
                <DropdownMenuItem key={key} onClick={() => setView(key)}
                  className={`text-zinc-200 focus:bg-zinc-800 focus:text-zinc-100 ${
                    view === key ? "bg-teal-500/10 text-teal-400" : ""
                  }`}
                  data-testid={`mobile-nav-${key}`}>
                  <Icon size={13} weight="duotone" className="mr-2" />
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={() => setView("upload")}
            className="border-zinc-700 hover:bg-zinc-800 h-8 px-2 xl:px-3" data-testid="upload-nav-btn"
            title="Upload">
            <Upload size={14} weight="duotone" />
            <span className="hidden xl:inline ml-1.5">Upload</span>
          </Button>

          <Button variant="outline" size="sm" onClick={() => setShowSettings(true)}
            className="border-zinc-700 hover:bg-zinc-800 h-8 px-2 xl:px-3" data-testid="settings-btn"
            title="Thresholds">
            <Gear size={14} weight="duotone" />
            <span className="hidden xl:inline ml-1.5">Thresholds</span>
          </Button>

          {isAdminRole && (
            <>
              {/* Admin tools dropdown - consolidates Pitch Deck, Parties, Routing, Webhooks, Team */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm"
                    className="border-zinc-700 hover:bg-zinc-800 h-8 px-2 xl:px-3"
                    data-testid="admin-tools-trigger" title="Admin Tools">
                    <DotsThreeVertical size={14} weight="bold" />
                    <span className="hidden xl:inline ml-1">Admin Tools</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-zinc-950 border-zinc-800 z-[2000]">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500">
                    Operational
                  </DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => setShowParties(true)} className="text-zinc-200 focus:bg-zinc-800 focus:text-zinc-100" data-testid="menu-parties">
                    <Buildings size={13} weight="duotone" className="mr-2 text-teal-400" />
                    Parties
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowDevices(true)} className="text-zinc-200 focus:bg-zinc-800 focus:text-zinc-100" data-testid="menu-devices">
                    <Cpu size={13} weight="duotone" className="mr-2 text-teal-400" />
                    Device Registry
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowGeofences(true)} className="text-zinc-200 focus:bg-zinc-800 focus:text-zinc-100" data-testid="menu-geofences">
                    <MapPinArea size={13} weight="duotone" className="mr-2 text-teal-400" />
                    Geofences
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowRouting(true)} className="text-zinc-200 focus:bg-zinc-800 focus:text-zinc-100" data-testid="menu-routing">
                    <Bell size={13} weight="duotone" className="mr-2 text-teal-400" />
                    Event Orchestration
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowWebhooks(true)} className="text-zinc-200 focus:bg-zinc-800 focus:text-zinc-100" data-testid="menu-webhooks">
                    <Webhook size={13} weight="duotone" className="mr-2 text-teal-400" />
                    Webhooks
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowLoggerWebhooks(true)} className="text-zinc-200 focus:bg-zinc-800 focus:text-zinc-100" data-testid="menu-logger-webhooks">
                    <Webhook size={13} weight="duotone" className="mr-2 text-teal-400" />
                    Inbound Logger Webhooks
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500">
                    Tenant
                  </DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => setShowInvites(true)} className="text-zinc-200 focus:bg-zinc-800 focus:text-zinc-100" data-testid="menu-team">
                    <UsersThree size={13} weight="duotone" className="mr-2 text-teal-400" />
                    Team & Invites
                  </DropdownMenuItem>
                  {isSuperAdmin && (
                    <DropdownMenuItem onClick={downloadPitchDeck} className="text-zinc-200 focus:bg-zinc-800 focus:text-zinc-100" data-testid="menu-pitch-deck">
                      <Presentation size={13} weight="duotone" className="mr-2 text-teal-400" />
                      Pitch Deck (.pptx)
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500">
                    Onboarding
                  </DropdownMenuLabel>
                  <DropdownMenuItem asChild>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/onboarding/quickstart.pdf`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center text-zinc-200 focus:bg-zinc-800 focus:text-zinc-100"
                      data-testid="menu-quickstart-pdf"
                    >
                      <FilePdf size={13} weight="duotone" className="mr-2 text-teal-400" />
                      New Operator Quick-Start (PDF)
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/onboarding/sample-logger.csv`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center text-zinc-200 focus:bg-zinc-800 focus:text-zinc-100"
                      data-testid="menu-sample-logger-csv"
                    >
                      <FileCsv size={13} weight="duotone" className="mr-2 text-teal-400" />
                      Sample Logger CSV
                    </a>
                  </DropdownMenuItem>
                  {isSuperAdmin && (
                    <>
                      <DropdownMenuSeparator className="bg-zinc-800" />
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-violet-400">
                        Platform · Super Admin
                      </DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => setShowSuperAdmin(true)}
                        className="text-zinc-200 focus:bg-zinc-800 focus:text-zinc-100"
                        data-testid="menu-super-admin">
                        <ShieldCheck size={13} weight="duotone" className="mr-2 text-violet-400" />
                        Tenants & Users
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {isSuperAdmin && <OrgSwitcher />}

              {isSuperAdmin && (
                <Button variant={view === "admin" ? "default" : "outline"} size="sm"
                  onClick={() => setView(view === "admin" ? "overview" : "admin")}
                  className={`h-8 px-2 xl:px-3 ${view === "admin" ? "bg-teal-500 text-zinc-950" : "border-zinc-700 hover:bg-zinc-800"}`}
                  data-testid="admin-panel-btn" title="Admin">
                  <ShieldCheck size={14} weight="duotone" />
                  <span className="hidden xl:inline ml-1.5">Admin</span>
                </Button>
              )}

              {isSuperAdmin && (
                <Button variant={view === "pilots" ? "default" : "outline"} size="sm"
                  onClick={() => setView(view === "pilots" ? "overview" : "pilots")}
                  className={`h-8 px-2 xl:px-3 ${view === "pilots" ? "bg-teal-500 text-zinc-950" : "border-zinc-700 hover:bg-zinc-800"}`}
                  data-testid="pilots-nav-btn" title="Pilot Management">
                  <ShieldCheck size={14} weight="duotone" />
                  <span className="hidden xl:inline ml-1.5">Pilots</span>
                </Button>
              )}
            </>
          )}

          <div className="flex items-center gap-2 ml-1 pl-2 border-l border-zinc-800">
            <span className="text-[10px] font-mono text-zinc-500 hidden lg:block">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={logout}
              className="text-zinc-500 hover:text-zinc-300 h-8 w-8 p-0" data-testid="logout-btn">
              <SignOut size={14} />
            </Button>
          </div>
        </div>
      </header>

      {/* Acting-as banner — shown only when super_admin has selected a non-home org */}
      <ActingAsBanner />

      {/* Force-password-change modal — blocks the app until rotated */}
      <ForcePasswordChangeModal />

      {/* Main Content */}
      <main className={view === "map" ? "" : "p-4 md:p-6"}>
        {view === "overview" && (
          <OverviewDashboard
            onViewMap={() => setView("map")}
            onViewDevices={() => setView("devices")}
            onViewShipments={() => setView("shipments")}
            onSelectDevice={handleSelectDevice}
            onSelectShipment={handleSelectShipment}
          />
        )}
        {view === "map" && (
          <FleetMapView onSelectDevice={handleSelectDevice}
            onSelectShipment={handleSelectShipment} />
        )}
        {view === "shipments" && (
          <ShipmentsView onSelectShipment={handleSelectShipment} />
        )}
        {view === "accountability" && (
          <AccountabilityDashboard isAdmin={isAdminRole} />
        )}
        {view === "alerts" && (
          <AlertCenter isAdmin={isAdminRole} />
        )}
        {view === "audit" && (
          <AuditTrailView onSelectShipment={handleSelectShipment} />
        )}
        {view === "shipment_detail" && selectedShipmentId && (
          <ShipmentDetail
            shipmentId={selectedShipmentId}
            onBack={() => setView("shipments")}
            onSelectDevice={handleSelectDevice}
          />
        )}
        {view === "devices" && (
          <DeviceListView
            onSelectDevice={handleSelectDevice}
            onSelectShipment={handleSelectShipment}
            onBack={() => setView("overview")}
          />
        )}
        {view === "detail" && selectedDatasetId && (
          <div>
            <button onClick={() => setView("devices")}
              className="text-xs font-mono text-teal-400 hover:text-teal-300 mb-4 flex items-center gap-1"
              data-testid="back-from-detail">
              ← Back to Devices
            </button>
            <Dashboard
              key={selectedDatasetId + (selectedDevice || "")}
              datasetId={selectedDatasetId}
              datasetName={selectedDevice || "Device Detail"}
              initialDevice={selectedDevice}
            />
          </div>
        )}
        {view === "upload" && (
          <FileUpload
            onComplete={(dataset) => {
              setView("overview");
              toast.success("Data uploaded successfully!");
            }}
            onCancel={() => setView("overview")}
            hasDatasets={true}
          />
        )}
        {view === "admin" && <AdminPanel />}
        {view === "pilots" && <PilotManagementPage initialEnrollOrgId={pendingEnrollOrgId} />}
      </main>

      <ThresholdSettings open={showSettings} onClose={() => setShowSettings(false)} />
      <InviteUsers open={showInvites} onClose={() => setShowInvites(false)} />
      <WebhooksManager open={showWebhooks} onClose={() => setShowWebhooks(false)} />
      <LoggerWebhookManager open={showLoggerWebhooks} onClose={() => setShowLoggerWebhooks(false)} />
      <PartiesManager open={showParties} onClose={() => setShowParties(false)} />
      <NotificationRulesManager open={showRouting} onClose={() => setShowRouting(false)} />
      <DeviceRegistry open={showDevices} onClose={() => setShowDevices(false)} />
      <GeofenceManager open={showGeofences} onClose={() => setShowGeofences(false)} />
      {showSuperAdmin && (
        <SuperAdminPanel
          onClose={() => setShowSuperAdmin(false)}
          onEnrollPilot={(orgId) => {
            setShowSuperAdmin(false);
            setPendingEnrollOrgId(orgId);
            setView("pilots");
          }}
        />
      )}
    </div>
    </PilotAcceptanceGate>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
